import { useState, useRef, useCallback, useMemo } from 'react';
import Formatter from '../../classes/Formatter/Formatter';



const MONTH_LABELS = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];

const YEAR_COLORS = [
  '#06E89F','#4ECDC4','#5869C5','#F5A623',
  '#E85D75','#9B59B6','#3498DB','#1ABC9C',
];

const CHART_MODE_LABELS = {
  monthly: 'Detalle mensual',
  grouped: 'Comparación por mes',
  annual: 'Resumen anual',
};

const METRICS_COPY = {
  monthly: 'Vista mensual de un solo año. Las barras naranjas superan el umbral crítico.',
  grouped: 'Comparación mes a mes entre los años elegidos.',
  annual: 'Con 4 o más años, el gráfico resume el total anual.',
};

const SENTINEL_YEARS = [2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];


const calcThreshold = (values) => {
  const max = Math.max(...values, 0);
  return Math.max(1, parseFloat((max * 0.6).toFixed(1)));
};

const getChartMode = (yearCount) => {
  if (yearCount <= 1) return 'monthly';
  if (yearCount <= 3) return 'grouped';
  return 'annual';
};

const AwaiLogic = (dmrvData) => {
  const [activeTab, setActiveTab] = useState('tab-0');
  const [sliderPos, setSliderPos] = useState(42);
  const [selectedYears, setSelectedYears] = useState([2022, 2023]);
  const [sentinelYear, setSentinelYear] = useState(2023);
  const imageCompareRef = useRef(null);
  const isDragging = useRef(false);

  const startDrag = useCallback(() => { isDragging.current = true; }, []);
  const stopDrag = useCallback(() => { isDragging.current = false; }, []);

  const onMouseMove = useCallback((e) => {
    if (!isDragging.current || !imageCompareRef.current) return;
    const rect = imageCompareRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    setSliderPos(Math.round((x / rect.width) * 100));
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!imageCompareRef.current) return;
    const touch = e.touches[0];
    const rect = imageCompareRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(touch.clientX - rect.left, rect.width));
    setSliderPos(Math.round((x / rect.width) * 100));
  }, []);


  const rawFeatures = useMemo(() => {
    return dmrvData?.monthly_trend?.features || [];
  }, [dmrvData]);

  const getMonthValue = useCallback((year, month) => {
    const entry = rawFeatures.find(
      (f) => f.properties.year === String(year) && f.properties.month === month
    );
    return entry ? parseFloat(Number(entry.properties.monthly_ha).toFixed(2)) : 0;
  }, [rawFeatures]);

  const availableYears = useMemo(() => {
    if (rawFeatures.length === 0) return [];
    return [...new Set(rawFeatures.map(f => Number(f.properties.year)))].sort();
  }, [rawFeatures]);

  const chartMode = getChartMode(selectedYears.length);

  const chartData = useMemo(() => {
    if (rawFeatures.length === 0) return null;

    if (chartMode === 'monthly') {
      const values = MONTH_LABELS.map((_, i) =>
        getMonthValue(selectedYears[0], String(i + 1).padStart(2, '0'))
      );
      const threshold = calcThreshold(values);
      return {
        labels: MONTH_LABELS,
        values,
        threshold,
        series: [{ name: 'Deforestación (ha)', data: values }],
        colors: values.map(v => v >= threshold ? '#F5A623' : '#06E89F'),
        distributed: true,
        columnWidth: '62%',
        showLegend: false,
        height: 280,
        summary: null,
      };
    }

    if (chartMode === 'grouped') {
      const sorted = [...selectedYears].sort();
      const series = sorted.map((year, idx) => ({
        name: String(year),
        data: MONTH_LABELS.map((_, i) =>
          getMonthValue(year, String(i + 1).padStart(2, '0'))
        ),
        color: YEAR_COLORS[idx % YEAR_COLORS.length],
      }));
      const allValues = series.flatMap(s => s.data);
      const threshold = calcThreshold(allValues);
      return {
        labels: MONTH_LABELS,
        threshold,
        series: series.map(s => ({ name: s.name, data: s.data })),
        colors: series.map(s => s.color),
        distributed: false,
        columnWidth: `${Math.max(55, 85 - sorted.length * 8)}%`,
        showLegend: true,
        height: 320,
        summary: null,
      };
    }


    const sorted = [...selectedYears].sort();
    const summary = sorted.map(year => {
      const months = MONTH_LABELS.map((label, i) => ({
        month: label,
        val: getMonthValue(year, String(i + 1).padStart(2, '0')),
      }));
      const total = parseFloat(months.reduce((s, m) => s + m.val, 0).toFixed(2));
      const peak = months.reduce((best, m) => m.val > best.val ? m : best, months[0]);
      return { year, total, peakMonth: peak.month, peakValue: peak.val };
    });
    const values = summary.map(s => s.total);
    const threshold = calcThreshold(values);
    return {
      labels: sorted.map(String),
      values,
      threshold,
      series: [{ name: 'Total anual (ha)', data: values }],
      colors: values.map(v => v >= threshold ? '#F5A623' : '#06E89F'),
      distributed: true,
      columnWidth: '55%',
      showLegend: false,
      height: 300,
      summary,
    };
  }, [chartMode, selectedYears, rawFeatures, getMonthValue]);

  const buildChartOptions = (data) => {
    if (!data) return {};
    return {
      chart: { type: 'bar', toolbar: { show: false }, background: 'transparent', foreColor: '#4a6a8a' },
      plotOptions: { bar: { borderRadius: 3, distributed: data.distributed, columnWidth: data.columnWidth } },
      colors: data.colors,
      legend: data.showLegend
        ? { show: true, position: 'top', horizontalAlign: 'left', fontSize: '11px', fontFamily: 'MontserratSemiBold', labels: { colors: '#8aa8c8' }, markers: { width: 10, height: 10, radius: 2 } }
        : { show: false },
      dataLabels: { enabled: false },
      grid: { borderColor: '#0d2040', strokeDashArray: 4 },
      annotations: {
        yaxis: [{
          y: data.threshold,
          borderColor: '#F5A623',
          borderWidth: 1,
          strokeDashArray: 6,
          label: {
            borderColor: 'transparent',
            style: { background: 'transparent', color: '#F5A623', fontSize: '9px', fontFamily: 'MontserratSemiBold' },
            text: `UMBRAL CRÍTICO (${data.threshold} HA)`,
            position: 'right',
            offsetX: -8,
          },
        }],
      },
      xaxis: {
        categories: data.labels,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#4a6a8a', fontSize: '10px', fontFamily: 'Montserrat' } },
      },
      yaxis: {
        labels: { style: { colors: '#4a6a8a', fontSize: '9px', fontFamily: 'Montserrat' }, formatter: v => `${Formatter.round(v)} ha` },
      },
      tooltip: { theme: 'dark', shared: chartMode === 'grouped', intersect: chartMode !== 'grouped', y: { formatter: v => `${v} ha` } },
    };
  };


  const { accLabels, accValues, totalHa } = useMemo(() => {
    const features = dmrvData?.accumulated_trend?.features || [];
    if (features.length === 0) return { accLabels: [], accValues: [], totalHa: '0.00' };

    const entries = features
      .map(f => f.properties)
      .sort((a, b) => a.date.localeCompare(b.date));

    const yearlyEntries = entries.filter(p => p.month === '12');
    const labels = yearlyEntries.map(p => p.year);
    const values = yearlyEntries.map(p => Number(p.cumulative_ha.toFixed(2)));
    const total = values.length > 0 ? values[values.length - 1].toFixed(2) : '0.00';

    return { accLabels: labels, accValues: values, totalHa: total };
  }, [dmrvData]);

  const accumulatedOptions = {
    chart: { type: 'area', toolbar: { show: false }, background: 'transparent', foreColor: '#4a6a8a' },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2, colors: ['#06E89F'] },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        colorStops: [
          { offset: 0, color: '#06E89F', opacity: 0.35 },
          { offset: 100, color: '#06E89F', opacity: 0.02 },
        ],
      },
    },
    markers: { size: 4, colors: ['#06E89F'], strokeColors: '#071020', strokeWidth: 2, hover: { size: 6 } },
    grid: { borderColor: '#0d2040', strokeDashArray: 4 },
    xaxis: {
      categories: accLabels,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { rotate: -45, style: { colors: '#4a6a8a', fontSize: '9px', fontFamily: 'Montserrat' } },
    },
    yaxis: {
      labels: { style: { colors: '#4a6a8a', fontSize: '10px', fontFamily: 'Montserrat' }, formatter: v => `${v} ha` },
    },
    tooltip: { theme: 'dark', y: { formatter: v => `${v} ha` } },
  };

  const accumulatedSeries = [{ name: 'Acumulada (ha)', data: accValues }];
  const monthlyBarOptions = buildChartOptions(chartData);
  const monthlyBarSeries = chartData?.series || [];
  const chartHeight = chartData?.height || 280;
  const annualSummary = chartData?.summary || null;
  const chartModeLabel = CHART_MODE_LABELS[chartMode];
  const metricsDescription = METRICS_COPY[chartMode];
  const yearRangeLabel = selectedYears.length === 1
    ? String(selectedYears[0])
    : `${Math.min(...selectedYears)} – ${Math.max(...selectedYears)}`;

  return {
    activeTab,
    setActiveTab,
    sliderPos,
    imageCompareRef,
    startDrag,
    stopDrag,
    onMouseMove,
    onTouchMove,
    monthlyBarSeries,
    monthlyBarOptions,
    accumulatedSeries,
    accumulatedOptions,
    selectedYears,
    setSelectedYears,
    yearRangeLabel,
    availableYears,
    totalHa,
    chartMode,
    chartModeLabel,
    chartHeight,
    annualSummary,
    metricsDescription,
    sentinelYear,
    setSentinelYear,
    SENTINEL_YEARS,
  };
};

export default AwaiLogic;
